import React, { useState, useEffect, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import { showSuccess, showError } from "toast/toast";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import Select, { SingleValue, components } from "react-select";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { StatusBadge } from "components/Productlocationstatus";
import { all } from "axios";
import InstallationStatusTracker from "components/InstallationStatusTracker";
import SectionHeader from "components/SectionPillPropsIcons";
import { Addresses } from "types";
import AddressFormModal from "components/AddAddressFormModal";
import InstructionsCreateCard from "components/Notes";
import ProductSelection from "assets/images/brands/ProductSelection.png"
import Doller from "assets/images/brands/doller.png"
import OrderNotes from "assets/images/brands/OrderNotes.png"
import SpecialFeeui from "components/Tax/InstallationandremovalSpecialfeeui";
import PONumber from "components/PONumber";
import { OrderDiscountCodeField } from "components/orderResolve";
import { handleApiError } from "utils/errorHandler";
import {
    applyRemovalWorkOrderResolveResponse,
    buildRemovalResolvePayload,
    getOrderDiscountFieldDisplay,
    mapInstallationSpecialFeeForResolve,
    mapInstallationSpecialFeeForSubmit,
    mapSavedInstallationSpecialFeeToUi,
    normalizeInstallationSelectedFee,
    overlayRemovalSubmitFromResolved,
    postRemovalOrderResolve,
    type InstallationResolveAddress,
    type ResolvedRemovalOrder,
} from "orderResolve";
import { Addresstax } from "types";
import { safeConsole, createSafeNavigate } from "utils/envUtils";
import { buildSpecialFeeCostBreakdownDisplay } from "utils/specialFeeCostBreakdownDisplay";
import { getAuthCookie } from "../../utils/authCookies";
import { sendRemovalOrderTriggerEmail } from "utils/salesOrderTriggerEmailEngine";
const mapContainerStyle = {
    width: "100%",
    height: "350px",
    borderRadius: "12px",
    overflow: "hidden",
    // boxShadow: "0 0 10px rgba(0,0,0,0.1)", // Soft shadow
    // border: "1px solid #dee2e6",          // Light border
};
const center = {
    lat: 40.712776, // Default latitude (e.g., New York City)
    lng: -74.005974, // Default longitude
};

/** Show a readable label for generated custom-product SKUs. */
function displayRemovalSku(item: { sku?: string; custom_sku?: string; inventory_source?: string }): string {
    if (String(item.inventory_source) !== "custom") return String(item.sku ?? "");

    const customSku = String(item.custom_sku ?? item.sku ?? "").trim();
    if (!customSku) return "";

    if (/^custom_sku(?:_|$)/i.test(customSku)) {
        return "Custom SKU";
    }

    return customSku;
}

type Selected = {
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
}

// Select2 types for team members
type BrokerageTeamMemberOption = {
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    offices: any[]; // Array of office objects from API
};

const DropdownIndicator = (props: any) => {
    return (
        <components.DropdownIndicator {...props}>
            <i className="ri-arrow-down-s-line fs-5" style={{ color: '#6c757d' }}></i>
        </components.DropdownIndicator>
    );
};

// Custom styles to ensure dropdown indicator is always visible
const customSelectStyles = {
    dropdownIndicator: (provided: any, state: any) => ({
        ...provided,
        display: 'flex !important',
        opacity: 1,
        visibility: 'visible',
    }),
    indicatorSeparator: (provided: any, state: any) => ({
        ...provided,
        display: 'none',
    }),
};

const RealtorAddRemovalRequest = () => {
    const { id } = useParams(); // get ID from URL
    const location = useLocation();
    const isTeammember = location.pathname.includes('/teammember');
    const baseRoute = isTeammember ? '/teammember' : '/realtor';
    const token = getAuthCookie("sessionToken");
    const userId = getAuthCookie("userId");
    const userName = getAuthCookie("userName");
    const licenseType = getAuthCookie("licenseType");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>("");
    const [preview, setPreview] = useState<string | null>(null);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [quantity, setQuantity] = useState(1);
    const [addressObjects, setAddressObjects] = useState<any[]>([]);
    const [showAllocateModal, setShowAllocateModal] = useState(false);
    const [removalOrder, setRemovalOrder] = useState<any>(null);
    const [products, setProducts] = useState<any>(null);
    const [installer, setInstaller] = useState<any[]>([]);
    const [selectedInstaller, setSelectedInstaller] = useState<Selected | null>(null);
    const [previewFiles, setPreviewFiles] = useState<{ file: File; url: string }[]>([]);
    const [uploadedFileMeta, setUploadedFileMeta] = useState<any[]>([]);
    const [removalQuantities, setRemovalQuantities] = useState<Record<string, number>>({});
    const [removalCost, setRemovalCost] = useState<number | null>(null);
    const [complietInstallationOrders, setComplietInstallationOrders] = useState<any[]>([]);
    const [teamMembers, setTeamMembers] = useState<any[]>([]);
    const [instructions, setInstructions] = useState<string>("");
    const [removalTrackingSummary, setRemovalTrackingSummary] = useState<any>(null);

    // ✅ State for special fees (bypasses Formik timing issues)
    const [selectedSpecialFees, setSelectedSpecialFees] = useState<any[]>([]);
    const [allAvailableSpecialFees, setAllAvailableSpecialFees] = useState<any[]>([]);
    const [isRecomputing, setIsRecomputing] = useState(false);

    // State for banner below widget
    const [zeroOrNegativeMsg, setZeroOrNegativeMsg] = React.useState<string>("");
    const [couponInput, setCouponInput] = React.useState("");
    const [liveDiscountDisplay, setLiveDiscountDisplay] = React.useState({ code: "", amount: 0 });
    const lastResolvedRemovalRef = React.useRef<ResolvedRemovalOrder | null>(null);
    const userClearedCouponRef = React.useRef(false);
    const isUpdatingFromRecomputeRef = React.useRef(false);
    const lastRecomputeParamsRef = React.useRef("");
    const recomputeRequestIdRef = React.useRef(0);
    const pendingRecomputeRef = React.useRef(false);
    const [dynamicCostId, setDynamicCostId] = React.useState<string>();
    const [dynamicTaxable, setDynamicTaxable] = React.useState<boolean>(false);
    const [showTaxDetails, setShowTaxDetails] = useState(false);

    // State for parent signage firm
    const [parentSignageId, setParentSignageId] = useState<string | null>(null);
    const [parentSignageName, setParentSignageName] = useState<string | null>(null);

    // Select2 API configuration
    const PAGE_SIZE = 10;

    const [selectedBillingAddress, setSelectedBillingAddress] =
        useState<Addresses | null>(null);
    const [selectedOfficeBillingAddress, setSelectedOfficeBillingAddress] =
        useState<Addresses | null>(null);

    const [installationAddress, setInstallationAddress] = useState<Addresses>();

    const [selectedLocation, setSelectedLocation] = useState<{
        lat: number;
        lng: number;
    } | null>(null);
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string, // Replace with your API key
    });

    const navigate = useNavigate();
    const safeNavigate = createSafeNavigate(navigate);

    useEffect(() => {
        return () => {
            if (preview) URL.revokeObjectURL(preview);
        };
    }, [preview]);

    const addressOptions =
        addressObjects !== null
            ? addressObjects?.map((address) => ({
                ...address,
                value: address._id,
                label: `${address.label}: ${address.address_details.addressLine1}, ${address.address_details.city}, ${address.address_details.state}, ${address.address_details.zip ? address.address_details.zip : ''}`,
            }))
            : [];

    const complietInstallationOrdersOptions = complietInstallationOrders?.map(order => ({
        value: order._id,
        label: order.order_number,
    }));
    // selected installation option for AsyncPaginate
    const [selectedInstallationOption, setSelectedInstallationOption] = useState<{ value: string; label: string } | null>(null);

    // Remote loader for completed installation orders (search + pagination)
    const loadCompletedInstallationOrderOptions: LoadOptions<{ value: string; label: string }, any, { page: number }> = useCallback(
        async (inputValue, _loadedOptions, additional) => {
            const page = additional?.page ?? 1;
            try {
                const baseId = userId || "";
                const params = new URLSearchParams({
                    q: (inputValue || "").trim(),
                    page: String(page),
                    limit: String(PAGE_SIZE),
                });
                const url = `${process.env.REACT_APP_API_URL}/installation_orders/search/${baseId}?${params.toString()}`;
                const res = await fetch(url, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });
                if (!res.ok) return { options: [], hasMore: false, additional: { page } };
                const data = await res.json();
                const list = data?.results ?? data?.orders ?? data?.data ?? [];
                const options = (Array.isArray(list) ? list : []).map((o: any) => ({
                    value: o.id ?? o._id ?? "",
                    label: o.text ?? o.order_number ?? o.label ?? String(o.id ?? o._id ?? ""),
                }));
                const hasMore = typeof data?.pagination?.more === "boolean"
                    ? !!data.pagination.more
                    : data?.totalPages
                        ? page < (data.totalPages || 1)
                        : data?.total
                            ? page * (data?.limit ?? PAGE_SIZE) < data.total
                            : options.length === PAGE_SIZE;
                return { options, hasMore, additional: { page: page + 1 } };
            } catch (err) {
                safeConsole.error("Failed to load completed installation orders:", err);
                return { options: [], hasMore: false, additional: { page } };
            }
        },
        [token, PAGE_SIZE, userId]
    );

    // Removed teamMemberOptions - now using AsyncPaginate with loadTeamMemberOptions

    // Fetch removal tracking summary
    const fetchRemovalTrackingSummary = async (orderId: string) => {
        if (!orderId) {
            // console.log("No installation order number available yet");
            return;
        }

        const url = `${process.env.REACT_APP_API_URL}/removal_tracking/installation_order/${orderId}/summary`;
        try {
            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = await response.json();

            if (!response.ok) {
                safeConsole.error("Failed to fetch removal tracking summary:", data.error);
                return;
            }

            setRemovalTrackingSummary(data);
            // console.log("Fetched removal tracking summary:", data);
        } catch (err) {
            safeConsole.error("Error fetching removal tracking summary:", err);
            await handleApiError(err, {
                module: "removal",
                action: "Realtor_Add_Removal_Request_fetch_removal_tracking_summary",
                userId: userId || undefined,
                licenseType: licenseType || undefined,
                userName: userName || undefined,
                url,
                method: "GET",
                additionalInfo: { orderId },
            }, token);
        }
    };

    // Helper function to get already removed quantity for a product
    const getAlreadyRemovedQuantity = (productId: string) => {
        if (!removalTrackingSummary) return 0;
        const summaryItem = removalTrackingSummary.find((item: any) =>
            item.key === productId && item.inventory_source !== "rotational"
        );
        return summaryItem ? summaryItem.total_removed : 0;
    };

    // Helper function to calculate available quantity for display
    const getAvailableQuantity = (item: any) => {
        const alreadyRemoved = getAlreadyRemovedQuantity(item.product_id);
        return Math.max(0, item.quantity - alreadyRemoved);
    };

    // const fetchComplietInstallationOrders = async () => {
    //     try {
    //         const response = await fetch(
    //             `${process.env.REACT_APP_API_URL}/installation_orders/search/${userId}`,
    //             {
    //                 method: "GET",
    //                 headers: {
    //                     Authorization: `Bearer ${token}`,
    //                     "Content-Type": "application/json",
    //                 },
    //             }
    //         );
    //         const data = await response.json();
    //         setComplietInstallationOrders(data.orders);
    //     } catch (err) {
    //         setError(
    //             err instanceof Error ? err.message : "An unknown error occurred"
    //         );
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    // useEffect(() => {
    //     fetchComplietInstallationOrders();
    // }, [token]);

    const fetchRemovalOrder = async (orderId: string | undefined) => {
        if (!orderId) return;
        const url = `${process.env.REACT_APP_API_URL}/installation_orders/${orderId}`;
        try {
            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.error || "Failed to fetch removal order");
            }
            setRemovalOrder(data);
            // console.log("Fetched removal order:", data);
            setProducts(data.line_items || []);

            // Initialize quantities after summary is loaded
            const initializeQuantities = async () => {
                await fetchRemovalTrackingSummary(data._id);

                const initialQuantities: Record<string, number> = {};
                data.line_items?.forEach((item: any) => {
                    if (item.inventory_source !== "rotational") {
                        // Only include products that have available quantity > 0
                        const alreadyRemoved = getAlreadyRemovedQuantity(item.product_id);
                        const availableQuantity = Math.max(0, item.quantity - alreadyRemoved);
                        if (availableQuantity > 0) {
                            initialQuantities[item.product_id] = 0; // default quantity
                        }
                    }
                });
                setRemovalQuantities(initialQuantities);
            };

            initializeQuantities();
        } catch (error: any) {
            // showError(error.message || "Unexpected error while fetching removal order.");
            await handleApiError(error, {
                module: "removal",
                action: "Realtor_Add_Removal_Request_fetch_removal_order",
                userId: userId || undefined,
                licenseType: licenseType || undefined,
                userName: userName || undefined,
                url,
                method: "GET",
                additionalInfo: { orderId },
            }, token);
        } finally {
            setLoading(false);
        }

    };

    useEffect(() => {
        fetchRemovalOrder(id);
    }, [id]);

    // defult removal address set
    useEffect(() => {
        if (removalOrder) {
            const addr = removalOrder?.installation_details?.installation_address;
            setInstallationAddress(addr);
        }
        if (removalOrder?.billing_address) {
            setSelectedBillingAddress(removalOrder?.billing_address)
        }
        if (removalOrder?.agent_id) {
            fetchAddresses(removalOrder?.agent_id)
        }
        else if (removalOrder?.office) {
            fetchAddresses(removalOrder?.office._id)
            setSelectedOfficeBillingAddress(removalOrder?.billing_address)
        }
    }, [removalOrder]);

    // seting defult location state to show the poiner in map
    useEffect(() => {
        if (
            installationAddress?.address_details?.latitude &&
            installationAddress?.address_details?.longitude &&
            installationAddress?.address_details?.latitude !== 0 &&
            installationAddress?.address_details?.longitude !== 0
        ) {
            setSelectedLocation({
                lat: installationAddress?.address_details?.latitude,
                lng: installationAddress?.address_details?.longitude,
            });
        }
    }, [installationAddress]);

    const fetchAddresses = async (id: any) => {
        const url = `${process.env.REACT_APP_API_URL}/user-addresses/${id}`;
        try {
            const response = await fetch(url, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            const data = await response.json();
            // setAddresses(data.addresses);
            setAddressObjects(data);
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "An unknown error occurred"
            );
            await handleApiError(err, {
                module: "removal",
                action: "Realtor_Add_Removal_Request_fetch_addresses",
                userId: userId || undefined,
                licenseType: licenseType || undefined,
                userName: userName || undefined,
                url,
                method: "GET",
                additionalInfo: { addressUserId: id },
            }, token);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchRemovalCost = async () => {
            if (
                !installationAddress?.address_details?.latitude ||
                !installationAddress?.address_details?.longitude ||
                installationAddress?.address_details?.latitude === 0 ||
                installationAddress?.address_details?.longitude === 0
            ) {
                showError(
                    "Selected removal address is missing latitude/longitude. Please enter the address manually."
                );
                return;
            }

            const url = `${process.env.REACT_APP_API_URL}/removal_orders/dynamic_cost/${removalOrder?.signage_id}`;
            const payload = {
                removal_address: {
                    addressLine1: installationAddress?.address_details?.addressLine1,
                    addressLine2: installationAddress?.address_details?.addressLine2,
                    city: installationAddress?.address_details?.city,
                    state: installationAddress?.address_details?.state,
                    country: installationAddress?.address_details?.country,
                    zip: installationAddress?.address_details?.zip,
                    cityCode: installationAddress?.address_details?.cityCode,
                    stateCode: installationAddress?.address_details?.stateCode,
                    countryCode: installationAddress?.address_details?.countryCode,
                    latitude: installationAddress?.address_details?.latitude,
                    longitude: installationAddress?.address_details?.longitude,
                }
            };

            try {
                const response = await fetch(url, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload),
                });

                const data = await response.json();
                if (!response.ok) throw new Error(data?.error || "Failed to fetch removal cost");

                setDynamicCostId(data.id);
                setDynamicTaxable(data.is_taxable || data.taxable);
                setRemovalCost(data.cost || 0);
            } catch (error: any) {
                safeConsole.error("Cost fetch error:", error.message);
                setRemovalCost(null);
                await handleApiError(error, {
                    module: "removal",
                    action: "Realtor_Add_Removal_Request_fetch_removal_cost",
                    userId: userId || undefined,
                    licenseType: licenseType || undefined,
                    userName: userName || undefined,
                    url,
                    method: "POST",
                    requestBody: payload,
                    additionalInfo: { signageId: removalOrder?.signage_id },
                }, token);
            }
        };

        if (installationAddress) {
            fetchRemovalCost();
        }
    }, [installationAddress, removalOrder?.signage_id, token, userId, licenseType, userName]);

    // Fetch parent hierarchy to find SIGNAGE_FIRM
    const fetchParentHierarchy = async () => {
        if (!token) return;

        const url = `${process.env.REACT_APP_API_URL}/parent-hierarchy`;
        try {
            const response = await fetch(url, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (response.ok) {
                const data = await response.json();

                // Find the parent with license_type "SIGNAGE_FIRM"
                const signageFirm = data.parent_chain?.find(
                    (parent: any) => parent.license_type === "SIGNAGE_FIRM"
                );

                if (signageFirm) {
                    setParentSignageId(signageFirm.id || signageFirm._id);
                    setParentSignageName(signageFirm.name);
                }
            }
        } catch (error) {
            safeConsole.error("Error fetching parent hierarchy:", error);
            await handleApiError(error, {
                module: "removal",
                action: "Realtor_Add_Removal_Request_fetch_parent_hierarchy",
                userId: userId || undefined,
                licenseType: licenseType || undefined,
                userName: userName || undefined,
                url,
                method: "GET",
            }, token);
        }
    };

    useEffect(() => {
        fetchParentHierarchy();
    }, []);

    // ===== Step 1: Normalize ID once when building Team Member options =====
    const normalizeId = (row: any) => String(row?.id ?? row?._id ?? "");

    const mapTeamMemberToOption = (row: any) => ({
        value: normalizeId(row), // ALWAYS store the id in value
        label: `${row?.firstName || ""} ${row?.lastName || ""}`.trim() || row?.text || "(no name)",
        firstName: row?.firstName || "",
        lastName: row?.lastName || "",
        email: row?.email || "",
        phone: row?.phone1 || "",
        offices: row?.offices || [], // Include offices from team member API response
    });

    // ===== Team Member Loader (Select2 style) =====
    const loadTeamMemberOptions: LoadOptions<BrokerageTeamMemberOption, any, { page: number }> = useCallback(
        async (inputValue, _loadedOptions, additional) => {
            const page = additional?.page ?? 1;
            // console.log("TeamMemberLoader firing with:", { page, inputValue });

            // Build URL with pagination and search
            const params = new URLSearchParams({
                licenseType: "BROKERAGE_TEAM_MEMBER",
                brokerageFirmID: removalOrder?.brokerage_firm_id || "",
                page: String(page),
                limit: String(PAGE_SIZE)
            });
            const search = (inputValue || "").trim();
            if (search) params.set("search", search);

            const url = `${process.env.REACT_APP_API_URL}/users-search?${params.toString()}`;

            try {
                const response = await fetch(url, {
                    headers: {
                        Accept: "application/json",
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });

                if (!response.ok) {
                    safeConsole.error("Failed to fetch team members:", response.statusText);
                    return { options: [], hasMore: false, additional: { page } };
                }

                const responseData = await response.json();
                // console.log('Team member API response:', { page, search, responseData });

                // Handle Select2-like API response format
                const results = responseData.results || [];
                const hasMore = responseData.pagination?.more || false;

                // Map to react-select options using normalized ID
                const options: BrokerageTeamMemberOption[] = results.map(mapTeamMemberToOption);

                return {
                    options,
                    hasMore,
                    additional: { page: page + 1 },
                };
            } catch (error) {
                safeConsole.error("Error loading team member options:", error);
                await handleApiError(error, {
                    module: "removal",
                    action: "Realtor_Add_Removal_Request_load_team_member_options",
                    userId: userId || undefined,
                    licenseType: licenseType || undefined,
                    userName: userName || undefined,
                    url,
                    method: "GET",
                }, token);
                return { options: [], hasMore: false, additional: { page } };
            }
        },
        [removalOrder?.brokerage_firm_id, token, userId, licenseType, userName]
    );


    // --- unique draft id for "add installation" ---
    const UNIQUE_ID_KEY = "re_removal_unique_id";
    const UNIQUE_ID_KEY1 = localStorage.getItem("re_removal_unique_id");


    async function ensureDraftUniqueId(token?: string): Promise<string | null> {
        const url = `${process.env.REACT_APP_API_URL}/utils/new-unique-id`;
        try {
            // 1) if already cached, return it
            const existing = localStorage.getItem(UNIQUE_ID_KEY);
            if (existing) return existing;

            if (!token) return null;

            // 2) else fetch one from API and cache it
            const res = await fetch(url, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            if (!res.ok) return null;

            const data = await res.json().catch(() => ({}));
            const id = data.id || data?.data?.id || data?.data?.unique_id || data?.unique_id || null;
            if (!id) return null;

            localStorage.setItem(UNIQUE_ID_KEY, id);
            return id;
        } catch (error) {
            await handleApiError(error, {
                module: "removal",
                action: "Realtor_Add_Removal_Request_ensure_draft_unique_id",
                userId: userId || undefined,
                licenseType: licenseType || undefined,
                userName: userName || undefined,
                url,
                method: "GET",
            }, token);
            return null;
        }
    }




    const coalesceDestination = (): Addresstax => {
        const a: Addresstax | undefined = installationAddress?.address_details;
        return {
            addressLine1: a?.addressLine1 ?? "",
            city: a?.city ?? "",
            state: a?.stateCode ?? a?.state ?? "",
            zip: a?.zip ?? "",
            stateCode: a?.stateCode ?? a?.state ?? "",
        };
    };

    // ✅ Simple: Just use the state directly (no Formik reads!)
    const getSelectedFees = () => {
        return selectedSpecialFees
            .map((f) => normalizeInstallationSelectedFee(f, allAvailableSpecialFees))
            .filter((f) => f.id !== "");
    };

    const currentOwner =
        removalOrder?.agent_id
            ? { id: removalOrder.agent_id, name: removalOrder.agent_name, type: "agent" as const }
            : removalOrder?.office?._id
                ? { id: removalOrder.office._id, name: removalOrder.office.officeName, type: "office" as const }
                : null;

    // ✅ Separate logic for CouponDiscount customerId (different from currentOwner)
    const effectiveCouponCreatedById = React.useMemo(() => {
        let id = "";
        let source = "";

        // Priority: Agent > Team Member (for office path) > Fallback to userId
        if (removalOrder?.agent_id) {
            // Agent path: use agent ID
            id = removalOrder.agent_id;
            source = "Agent";
        } else if (removalOrder?.office?._id && removalOrder?.team_member_id) {
            // Office path with team member: use team member ID (NOT office ID)
            id = removalOrder.team_member_id;
            source = "Team Member (via Office)";
        } else {
            id = "";
            source = "User (Fallback)";
        }

        // console.log(`🎟️ [AddRemoval] Coupon customerId: ${id} (from ${source})`);
        return id;
    }, [removalOrder?.agent_id, removalOrder?.office?._id, removalOrder?.team_member_id,]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            userType: removalOrder?.user_type,
            userFirstName: "",
            userLastName: "",
            installation_order: "",
            userEmail: removalOrder?.user_email,
            userPhone: removalOrder?.user_phone,
            brokerageFirmsId: removalOrder?.brokerage_firm_id,
            brokerage_firm_admin: removalOrder?.brokerage_firm_admin,
            teamMemberId: removalOrder?.team_member_id,
            teamMemberName: removalOrder?.team_member_name || "",
            office: removalOrder?.office,
            inventorySource: removalOrder?.inventory_source,
            order_number: removalOrder?.order_number,
            priority: "",
            removal_date: "",
            assignedId: "",
            agentId: removalOrder?.agent_id,
            agent: removalOrder?.agent_name,
            officeId: removalOrder?.office?._id,
            removal_quantity: 0,
            billing_address: removalOrder?.billing_address,
            removal_address: removalOrder?.installation_details.installation_address,
            isSameAsBilling: !!removalOrder?.both_address_same,
            billingAddressId: removalOrder?.billing_address?._id,
            removalAddressId: removalOrder?.installation_details?.installation_address?._id,
            location: "",
            products: "",
            instructions: instructions,
            payment_method: "",
            po_number: "",
            image: null,
            localLawsChecked: false,
            subtotal: 0,
            total: removalOrder?.total_amount,
            tax: 0,
            discount: 0,
            discount_amount: 0,
            discount_code: "",
            gatecode: "",
            removal_cost: 0,
            removal_taxable: true,
            removal_tax_percentage: null,
            calc_removal_rate_str: "",
            calc_overall_rate_str: "",
            calc_fee_rate_str: "",
            destination: { addressLine1: "", city: "", state: "", zip: "", country: "US", stateCode: "", countryCode: "US" },
            specialfee: [] as Array<{
                id: string;
                name: string;
                amount: number;
                taxable?: boolean;
                tax_percentage?: number | null;
            }>,
            totals: { removal_subtotal: 0, removal_tax_amount: 0, specialfee_subtotal: 0, specialfee_tax_amount: 0, grand_total: 0, cartId: "", specialfee_id: [], specialfee_name: [] },
        },
        validationSchema: Yup.object({
            //   assignedId: Yup.string().required("Assign to is required"),
            userEmail: Yup.string()
                .email("Invalid email")
                .required("Email is required"),
            userPhone: Yup.string()
                .required("Phone number is required"),
            removal_date: Yup.string().required("removal_date date is required"),
            // installation_order: Yup.string()
            //     .required("Installation job order is required"),

            // ✅ Required dropdown for Removal Priority
            priority: Yup.string()
                .required("Removal priority is required"),

            // ✅ Billing address required
            billingAddressId: Yup.string()
                .required("Billing address is required"),
            // billing_address: Yup.string().required("Billing address is required"),
            // installation_address: Yup.string().required(
            //     "Installation address is required"
            // ),
            // image: Yup.mixed().required("Image is required"),
        }),
        onSubmit: async (values) => {
            // ✅ Validate required special fees BEFORE submission
            const requiredFees = allAvailableSpecialFees.filter((f: any) => f.required === true);
            const selectedFeeIds = selectedSpecialFees.map((f: any) => String(f.id));
            const missingRequiredFees = requiredFees.filter((f: any) => !selectedFeeIds.includes(String(f.id || f._id)));

            if (missingRequiredFees.length > 0) {
                const feeNames = missingRequiredFees.map((f: any) => f.name).join(", ");
                showError(`The following required special fees must be selected: ${feeNames}`);
                return; // Don't submit
            }
            if (
                !UNIQUE_ID_KEY1 ||
                UNIQUE_ID_KEY1 === "000000000000000000000000"
            ) {
                showError("UNIQUE_ID_KEY1 is missing or zeros. Please try again.");
                return; // stop further code
            }



            setFormSubmitting(true);
            let removalOrderPayload: any;
            let cleanedLineItems: any[] = [];
            try {
                const resolveOk = await recompute({ force: true });
                if (!resolveOk || !lastResolvedRemovalRef.current) {
                    showError("Pricing could not be calculated. Please try again.");
                    setFormSubmitting(false);
                    return;
                }

                const resolved = lastResolvedRemovalRef.current;
                const uiFees = selectedSpecialFees || [];
                const feeRows = uiFees.map((f) =>
                    mapInstallationSpecialFeeForSubmit(f, { catalog: allAvailableSpecialFees })
                );

                // Example file upload handling
                const imageUrl = values.image ? await uploadFile(values.image) : null;
                cleanedLineItems = products?.map((item: any) => {
                    const toRemove = removalQuantities[item.product_id] || 0;

                    // Find all serial allocations for this product from rotational items
                    let serial_allocations = [];
                    if (item.inventory_source !== "rotational") {
                        // For a normal product, find all serials where source_product_id === item.product_id in *all* products
                        serial_allocations = products?.filter((p: any) => p.inventory_source === "rotational" && Array.isArray(p.serial_allocations))?.flatMap((p: any) =>
                            p.serial_allocations?.filter((s: any) => s.source_product_id === item.product_id)?.map((s: any) => ({
                                bin_id: s.bin_id,
                                rack_id: s.rack_id,
                                serial_id: s.serial_id,
                                serial_code: s.serial_code,
                                warehouse_id: s.warehouse_id,
                                status: s.status,
                                zone_id: s.zone_id,
                                warehouse_name: s.warehouse_name,
                                zone_name: s.zone_name,
                                rack_name: s.rack_name,
                                bin_name: s.bin_name,
                            }))
                        );
                    } else if (Array.isArray(item.serial_allocations)) {
                        // For rotational products themselves (if you want to send them), include their own serials
                        serial_allocations = item.serial_allocations?.map((s: any) => ({
                            bin_id: s.bin_id,
                            rack_id: s.rack_id,
                            serial_id: s.serial_id,
                            serial_code: s.serial_code,
                            warehouse_id: s.warehouse_id,
                            status: s.status,
                            zone_id: s.zone_id,
                            warehouse_name: s.warehouse_name,
                            zone_name: s.zone_name,
                            rack_name: s.rack_name,
                            bin_name: s.bin_name,
                        }));
                    }

                    return {
                        product_id: item.product_id,
                        variant_id: item.variant_id || "",
                        product_name: item.product_name,
                        inventory_source: item.inventory_source,
                        rotational_allocated: item.rotational_allocated,
                        source_product_id: item.inventory_source === "rotational" ? item.serial_allocations[0].source_product_id : item.product_id,
                        sku: item.sku,
                        available_quantity: item.quantity - toRemove,
                        quantity_to_be_removed: toRemove,
                        restock_quantity: 0,
                        writeoff_quantity: 0,
                        reason_to_writeoff: "",
                        current_status: item.current_status,
                        unit_price: item.unit_price,
                        total_price: item.total_price,
                        discount: item.discount || 0,
                        tax_amount: item.tax_amount || 0,
                        is_rotational: item.is_rotational,
                        notes: [
                            {
                                value: values.instructions,
                                added_by: {
                                    id: userId,
                                    name: userName,
                                },
                                added_at: new Date().toISOString(),
                            },
                        ],
                        storage_locations: item.storage_locations || [],
                        serial_allocations,
                    };
                });
                const url = `${process.env.REACT_APP_API_URL}/removal_orders`;
                removalOrderPayload = {
                        // Construct your payload here based on form values + local state
                        user_type: values.userType,
                        user_email: values.userEmail,
                        user_name: values.userFirstName + " " + values.userLastName,
                        user_phone: values.userPhone,
                        order_number: null, // or generate dynamically
                        status: 100,
                        installation_order_id: removalOrder?._id,
                        installation_order_number: removalOrder?.order_number,
                        inventory_source: values.inventorySource,
                        source_id: removalOrder?.source_id,
                        brokerage_firm_id: removalOrder?.brokerage_firm_id,
                        brokerage_firm_name: removalOrder?.brokerage_firm_name,
                        brokerage_firm_admin: removalOrder?.brokerage_firm_admin,
                        team_member_id: values.teamMemberId,
                        team_member_name: values.teamMemberName,
                        office: removalOrder?.office,
                        agent_id: values.agentId,
                        agent_name: removalOrder?.agent_name,
                        signage_id: removalOrder?.signage_id,
                        signage_company_name: removalOrder?.signage_company_name,
                        line_items: cleanedLineItems,
                        customer_group_id: null,
                        payment_method: values.payment_method,
                        payment_status: "Pending",
                        po_number: String(values.po_number || "").trim(),
                        payment_info: [], // or provide details
                        gate_access_code: values.gatecode || "",
                        removal_details: {
                            scheduled_date: new Date(values.removal_date).toISOString(),
                            removal_address: removalOrder?.installation_details.installation_address,
                            installer_id: removalOrder?.installation_details?.installer_id,
                            installer_name: removalOrder?.installation_details?.installer_name,
                            contact_number: removalOrder?.installation_details?.contact_number,
                            estimated_duration: null,
                            actual_removal_date: null,
                            additional_instructions: [
                                {
                                    value: values.instructions,
                                    added_by: {
                                        id: userId,
                                        name: userName,
                                    },
                                    added_at: new Date().toISOString(),
                                },
                            ],
                            assignee_id: removalOrder?.installation_details?.installer_id,
                            tax_details: null,
                        },
                        billing_address: removalOrder?.agent_id ? selectedBillingAddress : selectedOfficeBillingAddress,
                        both_address_same: values.isSameAsBilling,
                        additional_instructions: [
                            {
                                value: values.instructions,
                                added_by: {
                                    id: userId,
                                    name: userName,
                                },
                                added_at: new Date().toISOString(),
                            },
                        ],
                        local_laws_compliance: values.localLawsChecked,
                        priority: Number(values.priority),
                        uploaded_files: uploadedFileMeta || [],
                        subtotal_amount: Number(resolved.subtotal_amount ?? 0),
                        removal_cost: Number(resolved.removal_cost ?? removalCost ?? 0),
                        specialfee: feeRows,
                        additional_cost: Number(resolved.additional_cost ?? 0),
                        discount_amount: Number(resolved.discount_amount ?? 0),
                        discount_code: String(resolved.discount_code ?? ""),
                        total_amount: Number(resolved.total_amount ?? 0),
                        tax_status: String(resolved.tax_status ?? ""),
                        removal_tax_amount: Number(resolved.removal_tax_amount ?? 0),
                        removal_tax_percentage: resolved.removal_tax_percentage ?? undefined,
                        over_all_tax_amount: Number(resolved.over_all_tax_amount ?? 0),
                        over_all_tax_percentage: resolved.over_all_tax_percentage ?? "",
                        tax_inclusive: false,
                        cart_id: resolved.cart_id || UNIQUE_ID_KEY1 || undefined,
                        version: 1,
                        tax_details: null,
                    };

                removalOrderPayload = overlayRemovalSubmitFromResolved(
                    removalOrderPayload,
                    lastResolvedRemovalRef.current
                );

                const response = await fetch(url, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`, // ensure token is set
                    },
                    body: JSON.stringify(removalOrderPayload),
                });

                const data = await response.json();
                if (!response.ok) {
                    const userEmailError = data?.userEmail;
                    const userPhoneError = data?.userPhone;

                    if (userEmailError) {
                        formik.setFieldError("userEmail", userEmailError);
                        formik.setFieldTouched("userEmail", true, false);
                    }

                    if (userPhoneError) {
                        formik.setFieldError("userPhone", userPhoneError);
                        formik.setFieldTouched("userPhone", true, false);
                    }

                    if (userEmailError || userPhoneError) {
                        return;
                    }

                    showError(data.message || data.error || "Failed to create installation");
                    throw new Error(data.error || "Failed to create installation");
                }
                localStorage.removeItem("re_removal_unique_id");
                showSuccess("Removal request created successfully!");

                // ✅ Trigger 1: Brokerage Removal Request Created - Send notifications (only to end user and installer)
                try {
                  const { createNotification } = await import("../../services/notificationApi");
                  const { getBrokerageRemovalRequestCreatedContent } = await import("../../services/notificationContent");
                  const { fetchUserData } = await import("../../utils/userDataFetcher");
                  
                  // Fetch order details to get end user info
                  const orderResponse = await fetch(`${process.env.REACT_APP_API_URL}/removal_orders/${data.order_id}`, {
                    method: "GET",
                    headers: {
                      Authorization: `Bearer ${token}`,
                      "Content-Type": "application/json",
                    },
                  });

                  if (orderResponse.ok) {
                    const orderData = await orderResponse.json();
                    const orderNumber = data.order_number || data.order_id;
                    const installerId = selectedInstaller?.value || orderData?.installation_details?.installer_id || orderData?.signage_id;
                    let installerName = "Installer";
                    
                    if (installerId) {
                      try {
                        const installerData = await fetchUserData(installerId, token || "");
                        installerName = installerData?.companyName || installerData?.firmDetails?.companyName || installerData?.firstName || installerName;
                      } catch (err) {
                        safeConsole.warn("Failed to fetch installer data:", err);
                      }
                    }

                    // Use effectiveCouponCreatedById (the user who created the order)
                    const endUserId = effectiveCouponCreatedById || orderData?.agent_id || orderData?.team_member_id || orderData?.brokerage_firm_id || "";

                    // Send notification to single end user only
                    if (endUserId && token) {
                      try {
                        const endUserData = await fetchUserData(endUserId, token || "");
                        if (endUserData?.id) {
                          // Determine recipientType based on licenseType
                          let recipientType: "agent" | "brokerage_team_member" = "agent";
                          if (endUserData.licenseType === "BROKERAGE_FIRM" || endUserData.licenseType === "BROKERAGE_TEAM_MEMBER") {
                            recipientType = "brokerage_team_member";
                          } else if (endUserData.licenseType === "AGENT" || endUserData.licenseType === "BROKERAGE_AGENT") {
                            recipientType = "agent";
                          }

                          const endUserContent = getBrokerageRemovalRequestCreatedContent({
                            orderId: orderNumber,
                            brokerageName: installerName,
                            recipientType: recipientType,
                          });
                          await createNotification(endUserId, endUserContent, token, recipientType);
                        }
                      } catch (err) {
                        safeConsole.warn("Failed to send notification to end user:", err);
                      }
                    }

                    // Notify Installer only
                    if (installerId && token) {
                      try {
                        const installerData = await fetchUserData(installerId, token || "");
                        if (installerData?.id) {
                          const installerLicenseType = installerData?.licenseType || "SIGNAGE_FIRM";
                          const installerRecipientType = installerLicenseType === "SIGNAGE_TEAM_MEMBER" ? "team_member" : "signage_admin";

                          const installerContent = getBrokerageRemovalRequestCreatedContent({
                            orderId: orderNumber,
                            brokerageName: installerName,
                            recipientType: installerRecipientType,
                          });
                          await createNotification(installerId, installerContent, token, installerRecipientType);
                        }
                      } catch (err) {
                        safeConsole.warn("Failed to send notification to installer:", err);
                      }
                    }
                  }
                } catch (notifError) {
                  safeConsole.warn("Failed to send removal request created notification:", notifError);
                  // Don't block the order creation if notification fails
                }
                
                // Send "Removal request Created" email notifications (centralized)
                try {
                    const from = process.env.REACT_APP_FROM_EMAIL2 || "noreply@realesy.com";
                    const createdRemovalOrderId = String((data as any)?._id || (data as any)?.id || "").trim();
                    if (from && createdRemovalOrderId && token) {
                        await sendRemovalOrderTriggerEmail({
                            triggerName: "Brokerage Removal request Created",
                            removalOrderId: createdRemovalOrderId,
                            token,
                            fromEmail: from,
                            includeOrderPdfAttachment: true,
                        });
                    }
                } catch (emailError) {
                    safeConsole.error("Failed to send removal request creation emails:", emailError);
                }
                
                safeNavigate(
                    `${baseRoute}/jobs/editRemovalRequest/${data.order_id}`,
                    { state: { type: "View" } }
                );

            } catch (error: any) {
                safeConsole.error(error);
                showError(`Error: ${error.message}`);
                await handleApiError(error, {
                    module: "removal",
                    action: "Realtor_Add_Removal_Request_create_removal_order",
                    userId: userId || undefined,
                    licenseType: licenseType || undefined,
                    userName: userName || undefined,
                    url: `${process.env.REACT_APP_API_URL}/removal_orders`,
                    method: "POST",
                    requestBody: removalOrderPayload,
                    additionalInfo: {
                        installationOrderId: removalOrder?._id,
                        lineItemsCount: cleanedLineItems.length,
                    },
                }, token);
            } finally {
                setFormSubmitting(false);
                setUploadProgress(0);
            }
        },
    });

        // ✅ Call ensureDraftUniqueId when buyer type is set (from removalOrder or programmatically)
        React.useEffect(() => {
            const currentType = formik.values.userType;
            if (!currentType || !token) return;
    
            const ensureId = async () => {
                try {
                    const id = await ensureDraftUniqueId(token || "");
                    if (id) {
                        formik.setFieldValue("draft_unique_id", id, false);
                    }
                } catch (err) {
                    safeConsole.error("Error ensuring draft unique ID:", err);
                }
            };
    
            ensureId();
        }, [formik.values.userType, token]);

    React.useEffect(() => {
        const t = formik.values.totals || {};
        const subAll = Number(t.removal_subtotal || 0) + Number(t.specialfee_subtotal || 0);
        const taxAll = Number(t.removal_tax_amount || 0) + Number(t.specialfee_tax_amount || 0);
        const grand = Number(t.grand_total || 0);

        formik.setFieldValue("subtotal", subAll, false);
        formik.setFieldValue("tax", taxAll, false);

        // Final total = engine grand - coupon
        const final = Math.max(0, grand - Number(formik.values.discount_amount || 0));
        formik.setFieldValue("total", final, false);
    }, [
        formik.values.totals.removal_subtotal,
        formik.values.totals.specialfee_subtotal,
        formik.values.totals.removal_tax_amount,
        formik.values.totals.specialfee_tax_amount,
        formik.values.totals.grand_total,
        formik.values.discount_amount,
    ]);

    const getCurrentSubTotalAll = () => Number(removalCost || 0);

    // discount after amount = max(0, subtotal - discount_amount)
    const getDiscountedBase = () => {
        const subAll = getCurrentSubTotalAll();
        const disc = Number(formik.values.discount_amount || 0);
        return Math.max(0, subAll - disc);
    };

    const formikValuesRef = React.useRef(formik.values);
    React.useEffect(() => {
        formikValuesRef.current = formik.values;
    }, [formik.values]);

    const isRecomputingRef = React.useRef(false);

    type RecomputeOverride = { fees?: any[]; discountCode?: string; force?: boolean };

    const recompute = React.useCallback(async (override?: RecomputeOverride) => {
        if (!dynamicCostId || !token) return false;
        if (isRecomputingRef.current && !override?.force) return false;

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }

        const fees = override?.fees ?? getSelectedFees();
        const allFeesSubtotal = fees.reduce((sum: number, f: any) => sum + Number(f.amount || 0), 0);
        const discountCode =
            override?.discountCode !== undefined
                ? String(override.discountCode).trim()
                : String(
                      userClearedCouponRef.current
                          ? ""
                          : formikValuesRef.current.discount_code || couponInput
                  ).trim();

        const addr = installationAddress?.address_details;
        const requestSignature = JSON.stringify({
            dynamicCostId,
            removalCost: Number(removalCost) || 0,
            dynamicTaxable,
            discountCode,
            feeSig: fees.map((f: any) => `${f.id}:${Number(f.amount || 0)}`).sort().join(","),
            addressSig: addr
                ? `${addr.addressLine1}-${addr.city}-${addr.state}-${addr.zip}`
                : "",
        });

        if (!override?.force && lastRecomputeParamsRef.current === requestSignature) {
            return true;
        }

        recomputeRequestIdRef.current += 1;
        const currentRequestId = recomputeRequestIdRef.current;
        lastRecomputeParamsRef.current = requestSignature;

        isRecomputingRef.current = true;
        setIsRecomputing(true);
        try {
            if (currentRequestId !== recomputeRequestIdRef.current) return false;

            const billingAddress = removalOrder?.agent_id
                ? selectedBillingAddress
                : selectedOfficeBillingAddress;

            const resolvePayload = buildRemovalResolvePayload({
                cartId: String(UNIQUE_ID_KEY1 || formik.values.totals?.cartId || ""),
                dynamicCostId,
                signageId: String(removalOrder?.signage_id || ""),
                sourceId: String(removalOrder?.source_id || ""),
                customerId: String(effectiveCouponCreatedById || removalOrder?.source_id || ""),
                inventorySource: String(removalOrder?.inventory_source || "office"),
                removalCost: Number(removalCost) || 0,
                removalTaxable: dynamicTaxable,
                discountCode,
                paymentMethod: formikValuesRef.current.payment_method || "card",
                userType: formikValuesRef.current.userType,
                userName: `${formikValuesRef.current.userFirstName || ""} ${formikValuesRef.current.userLastName || ""}`.trim(),
                userEmail: formikValuesRef.current.userEmail,
                userPhone: formikValuesRef.current.userPhone,
                brokerageFirmId: removalOrder?.brokerage_firm_id ?? null,
                brokerageFirmName: removalOrder?.brokerage_firm_name ?? null,
                brokerageFirmAdmin: removalOrder?.brokerage_firm_admin ?? "",
                teamMemberId: formikValuesRef.current.teamMemberId || removalOrder?.team_member_id || null,
                teamMemberName: formikValuesRef.current.teamMemberName || removalOrder?.team_member_name || "",
                office: removalOrder?.office,
                agentId: formikValuesRef.current.agentId || removalOrder?.agent_id || null,
                agentName: removalOrder?.agent_name || null,
                billingAddress: billingAddress as InstallationResolveAddress | null,
                removalAddress: installationAddress as InstallationResolveAddress | null,
                installerId: String(selectedInstaller?.value || removalOrder?.installation_details?.installer_id || ""),
                installerName: String(selectedInstaller?.label || removalOrder?.installation_details?.installer_name || ""),
                lineItems: [],
                specialFees: fees.map((f: any) => mapInstallationSpecialFeeForResolve(f, allAvailableSpecialFees)),
            });

            const resolved = await postRemovalOrderResolve(token, {
                order_type: "removal",
                action: "draft",
                payload: resolvePayload,
            });

            if (currentRequestId !== recomputeRequestIdRef.current) return false;

            if (!resolved || typeof resolved !== "object") {
                formik.setFieldValue("totals.removal_subtotal", Number(removalCost) || 0, false);
                formik.setFieldValue("totals.removal_tax_amount", 0, false);
                formik.setFieldValue("totals.specialfee_subtotal", allFeesSubtotal, false);
                formik.setFieldValue("totals.specialfee_tax_amount", 0, false);
                formik.setFieldValue("totals.grand_total", (Number(removalCost) || 0) + allFeesSubtotal, false);
                formik.setFieldValue("specialfee", fees, false);
                return false;
            }

            lastResolvedRemovalRef.current = resolved;
            isUpdatingFromRecomputeRef.current = true;
            const applied = applyRemovalWorkOrderResolveResponse(
                resolved,
                formik,
                Number(removalCost) || 0,
                {
                    requestedDiscountCode: discountCode,
                    existingFeeRows: fees,
                    specialFeeCatalog: allAvailableSpecialFees,
                }
            );
            setSelectedSpecialFees(applied.mergedFees);
            formik.setFieldValue("specialfee", applied.mergedFees, false);
            setLiveDiscountDisplay(
                getOrderDiscountFieldDisplay({
                    formikCode: formikValuesRef.current.discount_code,
                    formikAmount: formikValuesRef.current.discount_amount,
                    couponInput,
                    resolved: resolved as Parameters<typeof getOrderDiscountFieldDisplay>[0]["resolved"],
                })
            );
            if (discountCode) {
                setCouponInput(discountCode);
            } else if (!formikValuesRef.current.discount_code) {
                setCouponInput("");
            }
            return true;
        } catch (e) {
            safeConsole.error("[OrderResolve] realtor removal add failed:", e);
            return false;
        } finally {
            isRecomputingRef.current = false;
            setIsRecomputing(false);
            if (pendingRecomputeRef.current) {
                pendingRecomputeRef.current = false;
                setTimeout(() => {
                    void recompute({ force: true });
                }, 0);
            }
        }
    }, [
        token,
        dynamicCostId,
        removalCost,
        dynamicTaxable,
        couponInput,
        removalOrder,
        selectedInstaller?.value,
        selectedInstaller?.label,
        selectedBillingAddress,
        selectedOfficeBillingAddress,
        installationAddress,
        effectiveCouponCreatedById,
        UNIQUE_ID_KEY1,
        formik,
    ]);

    const ready = React.useMemo(
        () =>
            Boolean(
                removalOrder?.signage_id &&
                installationAddress?.address_details &&
                !Number.isNaN(removalCost)
            ),
        [removalOrder?.signage_id, installationAddress?.address_details, removalCost]
    );

    const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const triggerRecomputeIfReady = React.useCallback(() => {
        if (!ready) return;

        if (isRecomputingRef.current) {
            pendingRecomputeRef.current = true;
            return;
        }

        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            if (!isRecomputingRef.current) {
                recompute();
            }
        }, 300);
    }, [ready, recompute]);

    React.useEffect(() => {
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, []);

    React.useEffect(() => {
        if (isRecomputingRef.current) return;
        triggerRecomputeIfReady();
    }, [
        installationAddress?.address_details?.addressLine1,
        installationAddress?.address_details?.city,
        installationAddress?.address_details?.state,
        installationAddress?.address_details?.zip,
        removalCost,
        dynamicCostId,
        dynamicTaxable,
        formik.values.discount_code,
        triggerRecomputeIfReady,
    ]);

    const prevFeesRef = React.useRef<string>("");
    React.useEffect(() => {
        if (isRecomputingRef.current) return;
        if (isUpdatingFromRecomputeRef.current) {
            isUpdatingFromRecomputeRef.current = false;
            return;
        }

        const feeSig = selectedSpecialFees
            .map((f) => `${f.id}:${Number(f.amount || 0)}`)
            .sort()
            .join(",");
        if (prevFeesRef.current === feeSig) return;
        prevFeesRef.current = feeSig;

        if (!ready) return;
        triggerRecomputeIfReady();
    }, [selectedSpecialFees, ready, triggerRecomputeIfReady]);

    const uploadFile = async (file: File): Promise<string> => {
        const url = `${process.env.REACT_APP_API_URL}/images/upload`;
        const formData = new FormData();
        formData.append("file", file);
        formData.append("uploadDir", "removal_requests");

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("POST", url, true);
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    setUploadProgress((event.loaded / event.total) * 100);
                }
            };

            xhr.onload = async () => {
                if (xhr.status === 200) {
                    const response = JSON.parse(xhr.responseText);
                    resolve(response.path);
                } else {
                    const error = new Error("Failed to upload file");
                    await handleApiError(error, {
                        module: "removal",
                        action: "Realtor_Add_Removal_Request_upload_file",
                        userId: userId || undefined,
                        licenseType: licenseType || undefined,
                        userName: userName || undefined,
                        url,
                        method: "POST",
                        additionalInfo: { fileName: file.name, fileSize: file.size, fileType: file.type, status: xhr.status },
                    }, token);
                    reject(error);
                }
            };

            xhr.onerror = async () => {
                const error = new Error("Network error during file upload");
                await handleApiError(error, {
                    module: "removal",
                    action: "Realtor_Add_Removal_Request_upload_file",
                    userId: userId || undefined,
                    licenseType: licenseType || undefined,
                    userName: userName || undefined,
                    url,
                    method: "POST",
                    additionalInfo: { fileName: file.name, fileSize: file.size, fileType: file.type },
                }, token);
                reject(error);
            };
            xhr.send(formData);
        });
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.currentTarget.files?.[0];
        if (file) {
            formik.setFieldValue("image", file);
            setPreview(URL.createObjectURL(file));
        }
    };

    const handleLocationChange = (
        selectedOption: SingleValue<{ value: string; label: string }>
    ) => {
        formik.setFieldValue("location", selectedOption?.value || "");
    };

    const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();
            setSelectedLocation({ lat, lng });
            formik.setFieldValue("location", { lat, lng });
        }
    }, []);

    // Note: featchBrokerageTeammembers function removed - now using AsyncPaginate with loadTeamMemberOptions

    const handleSaveInstructions = (newInstructions: string) => {
        setInstructions(newInstructions);
        formik.setFieldValue("instructions", newInstructions);
    };

    if (!isLoaded) return <div>Loading...</div>;

    return (
        <div className="content container">
            <div className="mb-4">
                <h3 className="m-0 fw-bold text-center addheaders">Add Removal Request</h3>
            </div>
            <div className="my-3">
                <div className="d-flex justify-content-between align-items-center flex-wrap col-lg-12 os-card" style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)', borderRadius: '12px', padding: '20px', backgroundColor: 'white' }}>
                    {/* Left: Removal Status Tracker */}
                    <div className="flex-grow-1">
                        <InstallationStatusTracker type="removal" status={0} className="w-100" />
                    </div>
                </div>
            </div>
            <form onSubmit={formik.handleSubmit} className="me-2 me-lg-0">
                <div className="row col-lg-12 card-body shadow-lg pb-4 mb-4 ms-0 p-3">
                    <div className="mb-3">
                        <SectionHeader title="Customer Information" icon="ri-user-3-line" color="#155dfc" size="md" divider />
                    </div>

                    <div className="col-lg-3 mb-3">
                        <label className="form-label table-cells">
                            Installation Job <span className="text-danger">*</span>
                        </label>
                        <AsyncPaginate
                            id="installation_order"
                            name="installation_order"
                            value={
                                selectedInstallationOption ||
                                (formik.values.installation_order
                                    ? { value: formik.values.installation_order, label: formik.values.order_number }
                                    : null)
                            }
                            loadOptions={loadCompletedInstallationOrderOptions}
                            onChange={async (selectedOption: any) => {
                                if (selectedOption) {
                                    setSelectedInstallationOption({ value: selectedOption.value, label: selectedOption.label });
                                    fetchRemovalOrder(selectedOption.value);
                                    formik.setFieldValue('order_number', selectedOption.label);
                                    formik.setFieldValue('installation_order', selectedOption.value);
                                    const id = await ensureDraftUniqueId(token || "");
                                    if (id) formik.setFieldValue("draft_unique_id", id, false);
                                } else {
                                    setSelectedInstallationOption(null);
                                    formik.setFieldValue('order_number', '');
                                    formik.setFieldValue('installation_order', '');
                                }
                            }}
                            additional={{ page: 1 }}
                            defaultOptions
                            debounceTimeout={300}
                            placeholder="Search and select an order..."
                            components={{ DropdownIndicator }}
                            styles={customSelectStyles}
                            classNamePrefix="rs"
                        />
                    </div>

                    <div className="col-lg-3 mb-3">
                        <label htmlFor="userType" className="form-label table-cells">
                            Buyer Type
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            id="userType"
                            name="userType"
                            value={formik.values.userType}
                            disabled
                            readOnly
                        />
                    </div>

                    {/* Brokerage Firm Name - Don't show for standalone agent orders */}
                    {removalOrder?.brokerage_firm_admin && !(formik.values.userType === "agent" && !removalOrder?.office?._id) && (
                        <div className="col-lg-3 mb-3">
                            <label className="table-cells">
                                Brokerage Firm
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={removalOrder?.brokerage_firm_admin || ""}
                                disabled
                                readOnly
                            />
                        </div>
                    )}

                    {/* Team Member Name */}
                    {removalOrder?.team_member_name && (
                        <div className="col-lg-3 mb-3">
                            <label className="table-cells">
                                Team Member
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={removalOrder?.team_member_name || ""}
                                disabled
                                readOnly
                            />
                        </div>
                    )}

                    {/* Office Name */}
                    {removalOrder?.office?.officeName && (
                        <div className="col-lg-3 mb-3">
                            <label className="table-cells">
                                Office
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={removalOrder?.office?.officeName || ""}
                                disabled
                                readOnly
                            />
                        </div>
                    )}

                    {/* Agent Name */}
                    {removalOrder?.agent_name && (
                        <div className="col-lg-3 mb-3">
                            <label className="table-cells">
                                Agent
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={removalOrder?.agent_name || ""}
                                disabled
                                readOnly
                            />
                        </div>
                    )}

                    <div className="col-lg-3 mb-3">
                        <label htmlFor="userEmail" className="form-label table-headers">Email <span className="text-danger">*</span></label>
                        <input
                            type="email"
                            className={`form-control table-cells ${(formik.submitCount > 0 || formik.touched.userEmail) && formik.errors.userEmail ? "is-invalid" : ""}`}
                            id="userEmail"
                            name="userEmail"
                            placeholder="Enter email"
                            value={formik.values.userEmail}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        />
                        {(formik.submitCount > 0 || formik.touched.userEmail) && formik.errors.userEmail && (
                            <div className="invalid-feedback d-block">{typeof formik.errors.userEmail === 'string' ? formik.errors.userEmail : ''}</div>
                        )}
                    </div>

                    <div className="col-lg-3 mb-3">
                        <label htmlFor="userPhone" className="form-label  table-headers">
                            Phone <span className="text-danger">*</span>
                        </label>
                        <input
                            type="text"
                            className={`form-control table-cells ${(formik.submitCount > 0 || formik.touched.userPhone) && formik.errors.userPhone ? "is-invalid" : ""}`}
                            id="userPhone"
                            name="userPhone"
                            placeholder="Enter phone"
                            value={formik.values.userPhone}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        />
                        {(formik.submitCount > 0 || formik.touched.userPhone) && formik.errors.userPhone && (
                            <div className="invalid-feedback d-block">{typeof formik.errors.userPhone === 'string' ? formik.errors.userPhone : ''}</div>
                        )}
                    </div>
                </div>

                <div className="d-block d-lg-flex gap-3">
                    <div className="col-lg-8">
                        <div className="row col-lg-12 card-body shadow-lg mb-4 ms-0 p-3">
                            <div className="mb-3">
                                <SectionHeader title="Schedule & Assignment" icon="ri-calendar-line" color="#00a63e" size="md" divider />
                            </div>
                            <div className="col-lg-4 mb-3">
                                <label className="form-label table-cells">
                                    Removal Date <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="date"
                                    min={new Date().toISOString().split("T")[0]}
                                    className={`form-control ${formik.touched.removal_date && formik.errors.removal_date ? "is-invalid" : ""
                                        }`}
                                    id="removal_date"
                                    name="removal_date"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.removal_date}
                                />
                                {formik.touched.removal_date && typeof formik.errors.removal_date === "string" && (
                                    <div className="invalid-feedback">{formik.errors.removal_date}</div>
                                )}
                            </div>
                            <div className="col-lg-4 mb-3">
                                <label className="form-label table-cells">
                                    Removal Priority <span className="text-danger">*</span>
                                </label>
                                <Select
                                    id="priority"
                                    name="priority"
                                    options={[
                                        { value: "3", label: "Normal" },
                                        { value: "2", label: "High Priority" },
                                        { value: "1", label: "Critical" },
                                    ]}
                                    onChange={(option: SingleValue<{ value: string; label: string }>) =>
                                        formik.setFieldValue("priority", option?.value || "")
                                    }
                                    components={{ DropdownIndicator }}
                                    styles={customSelectStyles}
                                    onBlur={formik.handleBlur}
                                />
                                {formik.touched.priority && formik.errors.priority && (
                                    <div className="text-danger">{formik.errors.priority}</div>
                                )}
                            </div>
                        </div>

                        <div className="row col-lg-12 mb-3 card-body shadow-lg ms-0 pb-3 p-3">
                            <div className="col-lg-12 d-flex mb-3">
                                {/* Address: teal */}
                                <SectionHeader title="Address Information" icon="ri-map-pin-line" color="#e7000b" size="md" divider className="col-lg-4" />
                                <div className="align-items-baseline col-lg-8 d-flex mb-3 justify-content-end">
                                    <div className="align-items-baseline col-lg-6 d-flex justify-content-end">
                                        <label className="me-2">Gate Code</label>
                                        <input type="text" id="gatecode" name="gatecode" className="form-control w-50" value={formik.values.gatecode} onChange={formik.handleChange} onBlur={formik.handleBlur} placeholder="Enter Gate Code" />
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-12">
                                {/* <div className="align-items-baseline col-lg-12 d-flex justify-content-between">
                                    <div className="my-2 fw-bold">
                                        <input
                                            type="checkbox"
                                            id="isSameAsBilling"
                                            name="isSameAsBilling"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            checked={formik.values.isSameAsBilling}
                                        />{" "}
                                        Billing and Installation Address are Same

                                    </div>
                                    <button
                                        className="btn btn-primary btn-sm mb-3"
                                        type="button"
                                        disabled={Boolean(formik.values.billingAddressId)}
                                        title={formik.values.billingAddressId ? "Billing address selected" : "Add new address"}
                                    >
                                        + Address
                                    </button>
                                </div> */}
                                <div className="row">
                                    <div className="col-lg-6 mb-3">
                                        <label
                                            htmlFor="billingAddressId"
                                            className="form-label table-cells"
                                        >
                                            Billing Address <span className="text-danger">*</span>
                                        </label>
                                        <Select
                                            // className="form-control"
                                            id="billingAddress"
                                            name="billingAddressId"
                                            //            classNames={{
                                            //     control: () => 'my-select-control',
                                            //     menu: () => 'my-select-menu',
                                            //     option: (state) =>
                                            //       `my-select-option ${state.isSelected ? 'is-selected' : ''} ${state.isFocused ? 'my-option-focused' : ''}`,
                                            //     container: () => 'my-select-container',
                                            //   }}
                                            options={addressOptions}
                                            value={addressOptions?.find((option) => option.value === formik.values.billingAddressId) || null}
                                            onChange={(selected: any) => {
                                                formik.setFieldValue("billingAddressId", selected?.value);
                                                const selectedAddress = addressObjects?.find(
                                                    (addr) => (addr.address_details ? addr.address_details._id : addr._id) === selected?.value
                                                );
                                                if (formik.values.officeId) {
                                                    setSelectedOfficeBillingAddress(selectedAddress || null);
                                                }
                                                else {
                                                    setSelectedBillingAddress(selectedAddress?.address_details || null);
                                                }
                                                formik.setFieldValue("isSameAsBilling", false);
                                            }}
                                            components={{ DropdownIndicator }}
                                            styles={customSelectStyles}
                                        />
                                        {formik.touched.billingAddressId &&
                                            typeof formik.errors.billingAddressId === "string" && (
                                                <div className="text-danger mt-1">{formik.errors.billingAddressId}</div>
                                            )}
                                    </div>
                                    {/* {formik.values.isSameAsBilling ? null : ( */}
                                    <div className="col-lg-6 mb-3">
                                        <label
                                            htmlFor="removalAddressId"
                                            className="form-label table-cells mt-2"
                                        >
                                            Removal Address <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={
                                                installationAddress?.address_details?.addressLine1 ||
                                                    installationAddress?.address_details?.city ||
                                                    installationAddress?.address_details?.state
                                                    ? [
                                                        installationAddress?.address_details?.addressLine1,
                                                        installationAddress?.address_details?.city,
                                                        installationAddress?.address_details?.state
                                                    ].filter(Boolean).join(', ')
                                                    : ''
                                            }
                                            placeholder="Removal Address"
                                            className="form-control"
                                            disabled
                                        />
                                    </div>
                                    {/* )} */}
                                </div>
                            </div>
                            <div className="col-lg-12">
                                <div style={mapContainerStyle}>
                                    <GoogleMap
                                        mapContainerStyle={mapContainerStyle}
                                        zoom={10}
                                        center={selectedLocation || center}
                                    // onClick={handleMapClick}
                                    >
                                        {selectedLocation && <Marker
                                            position={selectedLocation}
                                            draggable={false}
                                        // onDragEnd={handleMarkerDragEnd}
                                        />}
                                    </GoogleMap>
                                </div>
                            </div>
                        </div>

                        <div className="row col-lg-12 mb-5 card-body shadow-lg mb-3 ms-0 p-3">
                            <div className="d-flex mb-3">
                                <span
                                    aria-hidden="true"
                                    className="image-icons"
                                    style={{
                                        backgroundColor: "#9810fa",
                                        WebkitMaskImage: `url(${ProductSelection})`,
                                        maskImage: `url(${ProductSelection})`,
                                    }}
                                />
                                <SectionHeader title="Product Selection" icon="" variant="success" size="md" divider />
                            </div>
                            {/* Desktop Table View */}
                            <div className="col-lg-12 ms-2 d-none d-lg-block" id="row-overlap">
                                <table className="border table table-striped">
                                    <thead className="">
                                        <tr>
                                            <th className="text-center table-headers" style={{ width: "20%" }}>Product Name</th>
                                            <th className="text-center table-headers" style={{ width: "8%" }}>SKU</th>
                                            <th className="text-center table-headers" style={{ width: "16%" }}>Installation Qty </th>
                                            <th className="text-center table-headers" style={{ width: "12%" }}>Qty to remove </th>
                                            <th className="text-center table-headers" style={{ width: "20%" }}>Product Location Status</th>
                                            {/* <th className="text-center">Action</th> */}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {products?.filter((item: any) => {
                                            // Only show non-rotational products with remaining quantity > 0
                                            if (item.inventory_source === "rotational") {
                                                return false;
                                            } else {
                                                const alreadyRemoved = getAlreadyRemovedQuantity(item.product_id);
                                                const availableQuantity = Math.max(0, item.quantity - alreadyRemoved);
                                                return availableQuantity > 0;
                                            }
                                        })?.map((item: any, index: number) => (
                                            <tr key={index}>
                                                <td className="text-center table-cells text-ellipsis">{item.product_name}</td>
                                                <td className="text-center table-cells">{displayRemovalSku(item)}</td>
                                                <td className="text-center table-cells">{getAvailableQuantity(item)}</td>
                                                <td className="text-center table-cells">
                                                    <input
                                                        type="number"
                                                        className="form-control text-center table-cells"
                                                        value={removalQuantities[item.product_id] || ""}
                                                        placeholder="0"
                                                        onChange={(e) => {
                                                            const inputVal = parseInt(e.target.value) || 0;
                                                            const availableQty = getAvailableQuantity(item);
                                                            const clampedVal = Math.min(inputVal, availableQty);
                                                            setRemovalQuantities((prev) => ({
                                                                ...prev,
                                                                [item.product_id]: clampedVal,
                                                            }));
                                                        }}
                                                        onWheel={(e) => e.currentTarget.blur()}
                                                    />
                                                </td>
                                                <td className="text-center table-cells">
                                                    <span className="p-2">
                                                        <StatusBadge status={item.current_status?.replace(/_/g, " ") || "--"} />
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile/Tablet Card View */}
                            <div className="col-lg-12 d-lg-none">
                                <div className="row g-3">
                                    {products?.filter((item: any) => {
                                        // Only show non-rotational products with remaining quantity > 0
                                        if (item.inventory_source === "rotational") {
                                            return false;
                                        } else {
                                            const alreadyRemoved = getAlreadyRemovedQuantity(item.product_id);
                                            const availableQuantity = Math.max(0, item.quantity - alreadyRemoved);
                                            return availableQuantity > 0;
                                        }
                                    })?.map((item: any, index: number) => (
                                        <div key={index} className="col-12 mb-3">
                                            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                                                <div className="card-body p-3">
                                                    {/* Product Name - Top Row */}
                                                    <div className="mb-3">
                                                        <h6>Product Name</h6>
                                                        <h6 className="card-title mb-0 fw-medium">
                                                            {item.product_name}
                                                        </h6>
                                                    </div>

                                                    {/* Quantity Inputs - Second Row */}
                                                    <div className="row g-2 mb-3">
                                                        <div className="col-6">
                                                            <label className="form-label small mb-1">Installation Qty</label>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm text-center"
                                                                value={getAvailableQuantity(item)}
                                                                readOnly
                                                                style={{ backgroundColor: '#f8f9fa' }}
                                                            />
                                                        </div>
                                                        <div className="col-6">
                                                            <label className="form-label small mb-1">Qty to Remove</label>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm text-center"
                                                                value={removalQuantities[item.product_id] || ""}
                                                                placeholder="0"
                                                                min="0"
                                                                max={getAvailableQuantity(item)}
                                                                onChange={(e) => {
                                                                    const inputVal = parseInt(e.target.value) || 0;
                                                                    const availableQty = getAvailableQuantity(item);
                                                                    const clampedVal = Math.min(inputVal, availableQty);
                                                                    setRemovalQuantities((prev) => ({
                                                                        ...prev,
                                                                        [item.product_id]: clampedVal,
                                                                    }));
                                                                }}
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* SKU and Status - Bottom Row */}
                                                    <div className="row g-2">
                                                        <div className="col-6">
                                                            <h6>SKU</h6>
                                                            <span className="fw-medium">{displayRemovalSku(item)}</span>
                                                        </div>
                                                        <div className="col-6">
                                                            <h6>Product Location Status</h6>
                                                            <StatusBadge status={item.current_status?.replace(/_/g, " ") || "--"} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="row col-lg-12 mb-3">
                            <div className="col-lg-6 mb-3 mb-lg-0 pe-0">
                                <InstructionsCreateCard
                                    title="Instructions"
                                    // mode="instructions_files"
                                    loginType="signage"
                                    value={formik.values.instructions}
                                    onChange={(v) => formik.setFieldValue("instructions", v, false)}
                                    placeholder="Add instructions here"
                                    rows={4}
                                    className="mt-3"
                                    iconUrl={OrderNotes}
                                    iconColor="#f54a00"
                                    iconBackground="#EAF7F1"
                                />
                            </div>
                            <div className="align-items-center card-body col-lg-5 justify-content-end ms-0 ms-2 ms-lg-0 ms-lg-5 offset-1 p-4 shadow-lg">
                                <div className="align-items-center d-flex mb-3">
                                    <span
                                        aria-hidden="true"
                                        className="image-icons"
                                        style={{
                                            backgroundColor: "#00a63e",
                                            WebkitMaskImage: `url(${Doller})`,
                                            maskImage: `url(${Doller})`,
                                        }}
                                    />
                                    <SectionHeader title="Cost Breakdown" icon="" color="" size="lg" divider />
                                </div>
                                {removalOrder?.seller_owner_node_id && effectiveCouponCreatedById && (
                                    <>
                                        <OrderDiscountCodeField
                                            className="mb-2"
                                            value={couponInput}
                                            appliedCode={liveDiscountDisplay.code}
                                            discountAmount={liveDiscountDisplay.amount}
                                            disabled={isRecomputing}
                                            onChange={setCouponInput}
                                            onApply={(code: string) => {
                                                const trimmed = code.trim();
                                                if (!trimmed) return;
                                                userClearedCouponRef.current = false;
                                                setCouponInput(trimmed);
                                                formik.setFieldValue("discount_code", trimmed, false);
                                                void recompute({ force: true, discountCode: trimmed });
                                            }}
                                            onClear={() => {
                                                userClearedCouponRef.current = true;
                                                setCouponInput("");
                                                formik.setFieldValue("discount_code", "", false);
                                                formik.setFieldValue("discount_amount", 0, false);
                                                setZeroOrNegativeMsg("");
                                                void recompute({ force: true, discountCode: "" });
                                            }}
                                        />
                                        {zeroOrNegativeMsg && <div className="text-danger mt-2">{zeroOrNegativeMsg}</div>}
                                    </>
                                )}
                                <hr />

                                <div className="d-flex justify-content-between align-items-center py-2">
                                    <span className="fw-medium">Total Units:</span>
                                    <span className="fw-semibold">{Object.values(removalQuantities).reduce((sum, qty) => sum + qty, 0)}</span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center py-2">
                                    <span className="fw-medium">Removal Cost:</span>
                                    <span className="fw-semibold">${Number(removalCost).toFixed(2)}</span>
                                </div>

                                <div className="d-flex justify-content-between align-items-center py-2">
                                    <span className="fw-medium">Sub-total:</span>
                                    <span className="fw-semibold">
                                        ${Number(removalCost).toFixed(2)}
                                    </span>
                                </div>
                                {Number(formik.values?.discount_amount || 0) > 0 && (
                                    <div className="d-flex justify-content-between align-items-center py-2">
                                        <span className="fw-medium" style={{ color: "#00a63e" }}>Discount:{formik.values.discount_code}</span>
                                        <span className="fw-semibold text-success" style={{ color: "#00a63e" }}>
                                            -${Number(formik.values?.discount_amount || 0).toFixed(2)}
                                        </span>
                                    </div>
                                )}

                                <div className="d-flex justify-content-between align-items-center py-2">
                                    {(() => {
                                        const { labelText, tooltip } = buildSpecialFeeCostBreakdownDisplay(selectedSpecialFees);
                                        return (
                                            <>
                                                <span className="fw-medium me-2" style={{ minWidth: 0 }} title={tooltip}>
                                                    {labelText}:
                                                </span>
                                                <span className="fw-semibold flex-shrink-0">
                                                    ${Number(formik.values.totals.specialfee_subtotal).toFixed(2)}
                                                </span>
                                            </>
                                        );
                                    })()}
                                </div>

                                <div className="py-1">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <button
                                            type="button"
                                            onClick={() => setShowTaxDetails(v => !v)}
                                            className="btn btn-link p-0 text-decoration-none d-inline-flex align-items-center"
                                            aria-expanded={showTaxDetails}
                                            aria-controls="tax-breakdown"
                                        >
                                            {/* <i className={`bi ${showTaxDetails ? "bi-chevron-down" : "bi-chevron-right"} me-1`} /> */}
                                            <span className="order-card-title">Tax :</span>
                                        </button>
                                        <span className="order-card-cells">
                                            <span>
                                                ${(
                                                    Number(formik.values.totals.removal_tax_amount) +
                                                    Number(formik.values.totals.specialfee_tax_amount)
                                                ).toFixed(2)}
                                            </span>
                                        </span>
                                    </div>

                                    {/* {showTaxDetails && (
                                        <div id="tax-breakdown" className="border rounded-3 p-2 bg-light mt-1">
                                            <div className="d-flex justify-content-between small mb-1">
                                                <span>Removal Tax</span>
                                                <span className="order-card-cells">
                                                    ${Number(formik.values.totals.removal_tax_amount).toFixed(2)}
                                                </span>
                                            </div>
                                            <div className="d-flex justify-content-between small">
                                                <span>Special Fee Tax</span>
                                                <span className="order-card-cells">
                                                    ${Number(formik.values.totals.specialfee_tax_amount).toFixed(2)}
                                                </span>
                                            </div>
                                        </div>
                                    )} */}
                                </div>

                                <hr className="my-3" />

                                <div className="d-flex justify-content-between align-items-center py-2">
                                    <span className="fw-bold fs-5">Total Amount:</span>
                                    <span className="fw-bold fs-5 text-primary">
                                        ${(
                                            Number(removalCost || 0) +
                                            Number(formik.values?.totals?.specialfee_subtotal || 0) +
                                            Number(formik.values?.totals?.removal_tax_amount || 0) +
                                            Number(formik.values?.totals?.specialfee_tax_amount || 0) -
                                            Number(formik.values?.discount_amount || 0)
                                        ).toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="row mb-3 col-lg-12 rcust-margin-4 mt-4">
                            <div className="col-lg-12 d-block d-lg-flex justify-content-between">
                                {/* Left: Back */}
                                <div className="col-lg-2">
                                    <button
                                        className="btn back-btn surface-btn fw-bold fs-16 w-100"
                                        type="button"
                                        onClick={() => safeNavigate(-1)}
                                    >
                                        <i className="bi bi-chevron-left me-2" />
                                        Back
                                    </button>
                                </div>
                                {/* Right: checkbox + submit */}
                                <div className="d-block d-lg-flex align-items-baseline justify-content-end">
                                    <div className="col-lg-10 mt-3">
                                        <div className="d-flex d-lg-block form-check justify-content-center mb-3 mb-lg-0 gap-3">
                                            <input
                                                type="checkbox"
                                                id="localLaws"
                                                name="localLawsChecked"
                                                className="form-check-input"
                                                checked={formik.values.localLawsChecked}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                            <label htmlFor="localLaws" className="form-check-label mb-0 text-nowrap">
                                                I Acknowledge to local laws
                                            </label>
                                        </div>
                                    </div>
                                    <button
                                        type="submit"
                                        className="btn btn-primary save-btn surface-btn fw-bold fs-16 w-100"
                                        disabled={formSubmitting || isRecomputing || !formik.values.localLawsChecked}
                                    >
                                        {isRecomputing ? "Calculating..." : "Save Draft"}
                                    </button>
                                </div>
                            </div>
                        </div>
                        {/* <div className="row col-lg-12 mb-3 rcust-margin-4">
                            <div className="col-lg-12 d-flex justify-content-between">
                                <div className="col-lg-3">
                                    <button
                                        className="btn btn-outline-secondary br-8 backbutton"
                                        type="button"
                                        onClick={() => navigate(-1)}
                                    >
                                        Back
                                    </button>
                                </div>

                                <div className="col-lg-9 d-flex align-items-baseline justify-content-end">
                                    <div className="col-lg-4 mt-3">
                                        <div className="form-check">
                                            <input
                                                type="checkbox"
                                                id="localLaws"
                                                name="localLawsChecked"
                                                className="form-check-input"
                                                checked={formik.values.localLawsChecked}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                            <label htmlFor="localLaws" className="form-check-label ms-2">
                                                I Acknowledge to local laws
                                            </label>
                                        </div>
                                    </div>
                                    <button
                                        type="submit"
                                        className="btn btn-primary  px-4 table-cells"
                                        disabled={formSubmitting || !formik.values.localLawsChecked}
                                    >
                                        Save Draft
                                    </button>
                                </div>
                            </div>
                        </div> */}
                    </div>
                    {removalOrder?.seller_owner_node_id && effectiveCouponCreatedById && (
                        <div className="col-lg-3 card-body">
                            <PONumber
                                mode="create"
                                className="mb-3"
                                value={String(formik.values.po_number || "")}
                                onChange={(v) => formik.setFieldValue("po_number", v)}
                            />
                            {/* 👉 Special Fees UI */}
                            <SpecialFeeui
                                token={token || ""}
                                personId={effectiveCouponCreatedById || ""}
                                supplierId={removalOrder?.seller_owner_node_id || removalOrder?.signage_id || ""}
                                formik={formik}
                                name="specialfee"
                                apiBase={process.env.REACT_APP_API_URL || ""}
                                readOnly={false}
                                disabled={isRecomputing}  // ✅ Disable special fee selection when recompute is running
                                title="Special Fees"
                                onOptionsLoaded={(allOptions) => {
                                    // ✅ Receive all available fees (including required field) from component
                                    setAllAvailableSpecialFees(allOptions);
                                    // console.log("All available special fees from component:", allOptions);
                                }}
                                onUserChange={(nextFees) => {
                                    if (isRecomputingRef.current) {
                                        safeConsole.log("⏸️ Recompute in progress, skipping special fee change");
                                        return;
                                    }

                                    const currentSig = selectedSpecialFees
                                        .map((f) => `${f.id}:${Number(f.amount || 0)}`)
                                        .sort()
                                        .join(",");
                                    const nextSig = nextFees
                                        .map((f: any) => `${f.id}:${Number(f.amount || 0)}`)
                                        .sort()
                                        .join(",");

                                    setSelectedSpecialFees(nextFees);

                                    if (currentSig !== nextSig) {
                                        prevFeesRef.current = nextSig;
                                        void recompute({ fees: nextFees });
                                    }
                                }}
                            />
                        </div>
                    )}
                </div>
            </form >
        </div >
    );
};

export default RealtorAddRemovalRequest;




//    <div className="row mb-3">
//                             <div className="col-lg-6">
//                                 <div className="mb-3">
//                                     <label htmlFor="">Additional Instructions </label>
//                                     <textarea
//                                         name="instructions"
//                                         id="instructions"
//                                         className="form-control"
//                                         rows={4}
//                                         placeholder="Enter additional instructions"
//                                         value={formik.values.instructions}
//                                         onChange={formik.handleChange}
//                                         onBlur={formik.handleBlur}
//                                     ></textarea>
//                                 </div>
//                                 {/* <div className="mb-3">
//                                     <input type="checkbox" /> I acknowledge adherence to local laws.
//                                 </div>
//                                 <div className="row mb-3">
//                                     <div className="col-lg-8">
//                                         <label htmlFor="">Upload Files</label>
//                                         <input type="file" className="form-control" id="image" name="image" multiple />
//                                     </div>
//                                     <div className="col-lg-4 pt-4">
//                                         <button className="btn btn-success">Upload</button>
//                                     </div>
//                                 </div> */}
//                             </div>
//                             <div className="col-lg-6">
//                                 <table className="calculation-table mb-3">
//                                     <tr>
//                                         <th>Removal Fee</th>
//                                         <td><input type="text" name="removal_fee" id="removal_fee" value={Number(removalCost)?.toFixed(2) ?? 0.00} onChange={(e) => setRemovalCost(Number(e.target.value))} /></td>
//                                     </tr>
//                                     <tr>
//                                         <th>Subtotal</th>
//                                         <td>$ {Number(formik.values.subtotal).toFixed(2)}</td>
//                                     </tr>
//                                     <tr>
//                                         <th>Tax</th>
//                                         <td><input type="text" name="tax" id="tax" value={Number(formik.values.tax).toFixed(2)} onChange={formik.handleChange} onBlur={formik.handleBlur} /></td>
//                                     </tr>
//                                     <tr>
//                                         <th>Total</th>
//                                         <td>$ {Number(formik.values.total).toFixed(2)}</td>
//                                     </tr>
//                                 </table>
//                                 {/* <div className="mb-3">
//                                     <label htmlFor="">Payment Details</label>
//                                     <Select
//                                         id="installer"
//                                         name="installer"
//                                         placeholder="Select Payment Method"
//                                         options={[
//                                             { value: "Saved Card (**** **** **** 1234)", label: "Saved Card (**** **** **** 1234)" },
//                                             { value: "Add New Payment Method", label: "Add New Payment Method" },
//                                         ]}
//                                         onChange={(option) =>
//                                             formik.setFieldValue("installer", option?.value || "")
//                                         }
//                                         onBlur={formik.handleBlur}
//                                     />
//                                 </div> */}
//                             </div>
//                         </div>

//  <div className="row justify-content-end">
//                         <div className="col-lg-3 mt-3">
//                             <label className="me-1 mb-1">Local Laws Compliance :</label><br />
//                             <input
//                                 type="checkbox"
//                                 id="localLaws"
//                                 name="localLawsChecked"
//                                 checked={formik.values.localLawsChecked}
//                                 onChange={formik.handleChange}
//                                 onBlur={formik.handleBlur}
//                             />
//                             <span>{" "}I acknowledge adherence to local laws</span>

//                         </div>
//                     </div>

//                     <button
//                         type="submit"
//                         className="btn btn-info mt-4 float-end"
//                         disabled={formSubmitting || !formik.values.localLawsChecked}
//                     >
//                         Submit Request
//                     </button>
